import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { defineMessages, useIntl } from 'react-intl';
import { Container, List } from 'semantic-ui-react';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import Image from '@plone/volto/components/theme/Image/Image';

import { getMetgeImageUrl } from '../../helpers/quadreMedic';
import { appLinkTo } from '../../helpers/appUrl';

const messages = defineMessages({
  cmsHint: {
    id: 'Metge CMS view hint',
    defaultMessage:
      'La fitxa pública del professional es mostra al quadre mèdic del web, no en aquesta URL.',
  },
  editAgain: {
    id: 'Edit again',
    defaultMessage: 'Torna a editar',
  },
  especialitat: {
    id: 'Especialitat label',
    defaultMessage: 'Especialitat',
  },
  carrec: {
    id: 'Carrec label',
    defaultMessage: 'Càrrec',
  },
  anyIncorporacio: {
    id: 'Any incorporacio label',
    defaultMessage: 'Any d’incorporació',
  },
  reflexio: {
    id: 'Reflexio label',
    defaultMessage: 'Reflexió',
  },
  formacions: {
    id: 'Formacions label',
    defaultMessage: 'Formacions',
  },
  baixa: {
    id: 'Baixa label',
    defaultMessage: 'Baixa',
  },
  baixaYes: {
    id: 'Baixa yes',
    defaultMessage: 'Sí',
  },
  baixaNo: {
    id: 'Baixa no',
    defaultMessage: 'No',
  },
});

const normalizeFormacions = (formacions) => {
  if (!formacions) {
    return [];
  }
  if (Array.isArray(formacions)) {
    return formacions.filter(Boolean);
  }
  return [formacions].filter(Boolean);
};

const MetgeView = ({ content }) => {
  const intl = useIntl();
  const { pathname } = useLocation();
  const token = useSelector((state) => state.userSession.token);
  const displayName = content.cognomsnom || content.title || content.id;
  const imageUrl = getMetgeImageUrl(content);
  const formacions = normalizeFormacions(content.formacions);
  const editPath = appLinkTo(content['@id'], '/edit');

  return (
    <Container className="view-wrapper csj-metge-cms-view">
      <Helmet>
        <title>{displayName}</title>
      </Helmet>
      <p className="csj-metge-cms-hint">
        {intl.formatMessage(messages.cmsHint)}
      </p>
      {token && (
        <p>
          <Link to={editPath || `${pathname}/edit`}>
            {intl.formatMessage(messages.editAgain)}
          </Link>
        </p>
      )}
      {imageUrl && (
        <Image
          className="csj-metge-cms-photo"
          src={imageUrl}
          alt={displayName}
        />
      )}
      <h1 className="documentFirstHeading csj-heading1">{displayName}</h1>
      {content.especialitat && (
        <p>
          <strong>{intl.formatMessage(messages.especialitat)}:</strong>{' '}
          {content.especialitat}
        </p>
      )}
      {content.carrec && (
        <p>
          <strong>{intl.formatMessage(messages.carrec)}:</strong>{' '}
          {content.carrec}
        </p>
      )}
      {content.any_incorporacio != null && content.any_incorporacio !== '' && (
        <p>
          <strong>{intl.formatMessage(messages.anyIncorporacio)}:</strong>{' '}
          {content.any_incorporacio}
        </p>
      )}
      <p>
        <strong>{intl.formatMessage(messages.baixa)}:</strong>{' '}
        {content.baixa
          ? intl.formatMessage(messages.baixaYes)
          : intl.formatMessage(messages.baixaNo)}
      </p>
      {formacions.length > 0 && (
        <>
          <strong>{intl.formatMessage(messages.formacions)}</strong>
          <List bulleted>
            {formacions.map((item) => (
              <List.Item key={item}>{item}</List.Item>
            ))}
          </List>
        </>
      )}
      {content.reflexio && (
        <>
          <strong>{intl.formatMessage(messages.reflexio)}</strong>
          <p className="csj-metge-cms-reflexio">{content.reflexio}</p>
        </>
      )}
    </Container>
  );
};

MetgeView.propTypes = {
  content: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default MetgeView;
